// src/features/media/MergeUtils.ts

import { MERGE_MIN_VIDEOS, MERGE_MAX_DURATION_SECONDS } from '@/lib/constants/media';
import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { MediaUtils } from './MediaUtils';
import type { VideoViewData, VideoUpdateRequest, MergeCreateRequest } from '@/lib/types/Media';
import type { TranscodeVodData } from '@/lib/types/TranscodeVod';

export interface MergeFormState {
  title: string;
  descShort: string;
  tags: string[];
  mediaStateType: 'active' | 'inactive';
  published: number;
  transcodeVodId: number | null;
}

/** Pre-lowercased searchable record for the add-video Select (no label string —
 * labels are built only for the bounded set of matches actually rendered). */
export interface MergeAddCandidate {
  id: number;
  title: string;
  titleLower: string;
  duration: number;
}

export interface MergeValidation {
  isValid: boolean;
  statusMessage: string;
  statusColor: string;
}

/**
 * Pure utility functions for the video merge feature.
 * Dedicated builder avoids transformFormToUpdateRequest() which hardcodes adSupported: false.
 */
export class MergeUtils {
  /**
   * Build initial form state from source videos and transcode presets.
   * Title: "{first video title} merged"
   * Tags: deduplicated union of all source videos' tags
   * Published: current timestamp
   * TranscodeVodId: highest quality preset
   */
  static buildInitialMergeState(
    sourceVideos: VideoViewData[],
    transcodeVods: TranscodeVodData[] | undefined
  ): MergeFormState {
    const first = sourceVideos[0];
    const media = first.media;

    // Deduplicated union of all tags (case-insensitive)
    const tagSet = new Set<string>();
    const tagLower = new Set<string>();
    for (const video of sourceVideos) {
      for (const tag of video.tags) {
        const lower = tag.toLowerCase();
        if (!tagLower.has(lower)) {
          tagLower.add(lower);
          tagSet.add(tag);
        }
      }
    }

    // Auto-select highest quality transcode preset
    let transcodeVodId: number | null = null;
    if (transcodeVods && transcodeVods.length > 0) {
      const sorted = [...transcodeVods].sort((a, b) => {
        const aMax = Math.max(...a.outputs.map(o => o.videoVerticalSize ?? 0));
        const bMax = Math.max(...b.outputs.map(o => o.videoVerticalSize ?? 0));
        return bMax - aMax;
      });
      transcodeVodId = sorted[0].transcodeVodId;
    }

    return {
      title: `${media.title} merged`,
      descShort: media.descShort ?? '',
      tags: [...tagSet],
      mediaStateType: media.mediaStateType as 'active' | 'inactive',
      published: Math.floor(Date.now() / 1000),
      transcodeVodId,
    };
  }

  /**
   * One pass over the library per dataset change: Ready videos only, titles
   * pre-lowercased so per-keystroke searches don't re-lowercase 60k strings.
   */
  static buildAddCandidates(videosById: Map<number, VideoViewData>): MergeAddCandidate[] {
    const candidates: MergeAddCandidate[] = [];
    for (const [id, video] of videosById) {
      if (video.media.mediaStatusType !== MediaStatusType.Ready) continue;
      candidates.push({
        id,
        title: video.media.title,
        titleLower: video.media.title.toLowerCase(),
        duration: video.media.duration,
      });
    }
    return candidates;
  }

  /**
   * Word-substring match (every space-separated term must appear in the title,
   * same semantics as CustomerSelector) over the candidate index, excluding
   * videos already in the merge list, early-exiting at maxOptions. Empty search
   * returns the first maxOptions candidates. The bound is load-bearing: options
   * returned here are mounted in the Select's keepMounted dropdown and
   * reconciled on every modal render.
   */
  static searchAddCandidates(
    candidates: MergeAddCandidate[],
    excludeIds: ReadonlySet<number>,
    search: string,
    maxOptions: number
  ): { value: string; label: string }[] {
    const terms = search.toLowerCase().trim().split(' ').filter(Boolean);
    const options: { value: string; label: string }[] = [];
    for (const c of candidates) {
      if (excludeIds.has(c.id)) continue;
      if (terms.length > 0 && !terms.every((t) => c.titleLower.includes(t))) continue;
      options.push({
        value: String(c.id),
        label: `${c.title} (${MediaUtils.formatDuration(c.duration)})`,
      });
      if (options.length === maxOptions) break;
    }
    return options;
  }

  /**
   * Sum durations for the current ordered list of media IDs.
   */
  static computeTotalDuration(
    orderedMediaIds: number[],
    videosById: Map<number, VideoViewData>
  ): number {
    let total = 0;
    for (const id of orderedMediaIds) {
      const video = videosById.get(id);
      if (video) total += video.media.duration;
    }
    return total;
  }

  /**
   * Validate merge state and return status message/color for the UI.
   */
  static validateMergeState(
    sourceCount: number,
    title: string,
    totalDuration: number
  ): MergeValidation {
    if (sourceCount < MERGE_MIN_VIDEOS) {
      return {
        isValid: false,
        statusMessage: `${MERGE_MIN_VIDEOS} or more videos required`,
        statusColor: 'red',
      };
    }

    if (totalDuration > MERGE_MAX_DURATION_SECONDS) {
      return {
        isValid: false,
        statusMessage: 'Total duration must be less than 12 hours',
        statusColor: 'red',
      };
    }

    if (!title.trim()) {
      return {
        isValid: false,
        statusMessage: 'Title is required',
        statusColor: 'red',
      };
    }

    const formatted = MediaUtils.formatDuration(totalDuration);
    return {
      isValid: true,
      statusMessage: `${sourceCount} videos \u00b7 total duration ${formatted}`,
      statusColor: 'green',
    };
  }

  /**
   * Build the MergeCreateRequest payload.
   * Clones ALL metadata from the first (base) video, then overlays only user-editable fields.
   * Preserves adSupported, descLong, availability, related links, notes from base video.
   * Forces refId=null and refIdChanged=false for new content.
   */
  static buildMergeRequest(
    baseVideo: VideoViewData,
    formState: MergeFormState,
    orderedMediaIds: number[]
  ): MergeCreateRequest {
    const media = baseVideo.media;

    const updateMediaReq: VideoUpdateRequest = {
      // User-editable fields (from form)
      title: formState.title.trim(),
      descShort: formState.descShort || null,
      tags: formState.tags,
      mediaStateType: formState.mediaStateType,
      published: formState.published,

      // Inherited from base video (not in form)
      descLong: media.descLong ?? null,
      availStart: media.availStart ?? null,
      availEnd: media.availEnd ?? null,
      relatedLinkUrl: media.relatedLinkUrl ?? null,
      relatedLinkText: media.relatedLinkText ?? null,
      notes: media.notes ?? null,
      adSupported: media.adSupported,

      // Forced values for new merged content
      refId: null,
      refIdChanged: false,
    };

    return {
      mediaIds: orderedMediaIds,
      updateMediaReq,
      transcodeVodId: formState.transcodeVodId,
    };
  }
}
